import { metricWeights, qubitRange } from './config.js';

/**
 * Handles quantum zone calculations for qubit distribution
 */
export class ZoneCalculator {
  /**
   * Calculate dynamic zones based on quantum metrics
   * @param {Object} analysisData Complete quantum analysis data
   * @returns {Array<number>} Array of zone thresholds
   */
  static calculateZones(analysisData) {
    const {
      quantumState,
      particleActivity,
      cosmicEvents,
      signalSummary
    } = analysisData;

    // Extract and normalize quantum metrics
    const coherenceVar = parseFloat(quantumState.coherence) / 100;
    const entanglementVar = parseFloat(quantumState.entanglement) / 100;
    const resonanceVar = parseFloat(quantumState.resonance) / 100;
    
    // Get particle and cosmic influence
    const particleVar = particleActivity ? 
      parseFloat(particleActivity.confidence) / 100 : 0.5;
    const cosmicVar = cosmicEvents ? 
      parseFloat(cosmicEvents.significance) / 100 : 0.5;

    // Calculate zone multiplier using weighted metrics
    const zoneMultiplier = (
      coherenceVar * metricWeights.coherence +
      entanglementVar * metricWeights.entanglement +
      resonanceVar * metricWeights.resonance +
      particleVar * metricWeights.particle +
      cosmicVar * metricWeights.cosmic
    );

    // Get quality factor for zone adjustment
    const qualityFactor = parseFloat(signalSummary.quality) / 100;

    // Calculate dynamic zone thresholds
    const zoneThresholds = [
      0.2 * (1 + qualityFactor * 0.2),
      0.4 * (1 + qualityFactor * 0.15),
      0.6 * (1 + qualityFactor * 0.1),
      0.8 * (1 + qualityFactor * 0.05),
      1.0
    ];

    const range = qubitRange.max - qubitRange.min;

    // Map thresholds to actual qubit values
    return zoneThresholds.map(threshold => 
      Math.floor(qubitRange.min + (range * threshold * zoneMultiplier))
    );
  }

  /**
   * Calculate position within a zone
   * @param {number} baseValue Normalized base value
   * @param {Array<number>} zones Array of zone thresholds
   * @returns {Object} Zone information including index and position
   */
  static calculateZonePosition(baseValue, zones) {
    // Determine zone index
    const zoneIndex = Math.min(
      Math.floor(baseValue * 4),
      zones.length - 2
    );

    // Calculate position within zone
    const zonePosition = (baseValue * 4) % 1;

    // Get zone boundaries
    const zoneMin = zones[zoneIndex];
    const zoneMax = zones[zoneIndex + 1];

    return {
      index: zoneIndex,
      position: zonePosition,
      min: zoneMin,
      max: zoneMax,
      range: zoneMax - zoneMin
    };
  }

  /**
   * Validate and adjust final qubit value
   * @param {number} value Calculated qubit value
   * @returns {number} Validated qubit value within allowed range
   */
  static validateQubitValue(value) {
    return Math.min(
      Math.max(
        Math.floor(value),
        qubitRange.min
      ),
      qubitRange.max
    );
  }
}